import { useState, useEffect, useLayoutEffect, useRef } from "react";
import Header from "./header";
import { Banner } from "../../../../services/utilities";
import BodySwitcher from "./bodySwitcher";
import Signatories from "./signatories";
import "../../printout.css";
import Footer from "./footer";
import "./style.css";

function chunkArray(array, size) {
  const result = [];
  const entries = Object.entries(array);
  for (let i = 0; i < entries.length; i += size) {
    result.push(Object.fromEntries(entries.slice(i, i + size)));
  }
  return result;
}

// A4 paper in px (210mm x 297mm at 96dpi) plus the preview spacing
const PAGE_WIDTH = 794 + 32;
const PAGE_HEIGHT = 1123 + 32;

// scale of the screen preview so the whole page fits the window
const useFitToWindow = () => {
  const getFit = () =>
    Math.min(1, window.innerWidth / PAGE_WIDTH, window.innerHeight / PAGE_HEIGHT);
  const [fit, setFit] = useState(getFit);

  useEffect(() => {
    const handleResize = () => setFit(getFit());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return fit;
};

/**
 * Scale of the result so it always fits in one A4 page.
 * Measured once the banner is loaded, before printing.
 */
const useFitToPage = (pageRef, sheetRef, isReady) => {
  const [fit, setFit] = useState(1);

  useLayoutEffect(() => {
    const page = pageRef.current,
      sheet = sheetRef.current;
    if (!isReady || !page || !sheet) return;

    const { paddingTop, paddingBottom } = window.getComputedStyle(page);
    const available =
      page.clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
    const content = sheet.scrollHeight;
    if (available && content > available) setFit(available / content);
  }, [isReady, pageRef, sheetRef]);

  return fit;
};

const Printout = ({ task, onloaded, setOnloaded }) => {
  const { branchId, remarks, signatories, packages, hasValidator } = task;
  // one page only, same format as the previous chunks
  const [chunk] = chunkArray(packages, Infinity);
  const fit = useFitToWindow();
  const pageRef = useRef(null),
    sheetRef = useRef(null);
  const pageFit = useFitToPage(pageRef, sheetRef, onloaded);
  return (
    <div
      className="laboratory-container"
      style={{ "--laboratory-fit": fit, "--laboratory-page-fit": pageFit }}
    >
      {[chunk || {}].map((chunk, index) => (
        <div key={index} ref={pageRef} className="laboratory-page">
          <div ref={sheetRef} className="laboratory-sheet">
          <div className="laboratory-page-content">
            <Banner
              company={branchId.companyId.name}
              branch={branchId.name}
              bid={branchId?.bid || ""}
              onloaded={onloaded}
              setOnloaded={setOnloaded}
              className="laboratory-banner"
            />
            <div className="laboratory-body">
              <Header task={task} />
              <BodySwitcher
                task={{ ...task, packages: chunk, data: packages }}
              />
            </div>
          </div>

          <div className="laboratory-footer">
            <div className="laboratory-remarks d-flex px-1">
              <div style={{ paddingTop: "2px" }} className="mr-1 mb-1">
                <span className="ml-2">Remarks:</span>
              </div>
              <h5 className="fw-bold">{remarks}</h5>
            </div>
            <div className="laboratory-line" />
            <Signatories
              signatories={signatories}
              hasValidator={hasValidator}
            />
            {task?.isDuplicate && (
              <h6
                style={{ marginTop: "-2rem", fontWeight: 400 }}
                className="ml-2"
              >
                Duplicate Copy
              </h6>
            )}
            <Footer dealId={task?._id} />
          </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function LabTaskPrintout() {
  const [task, setTask] = useState({ _id: "" });
  const [onloaded, setOnloaded] = useState(false);

  useEffect(() => {
    setTask(JSON.parse(localStorage.getItem("taskPrintout")));
  }, []);

  useEffect(() => {
    if (onloaded) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [onloaded]);

  if (task?._id)
    return (
      <Printout task={task} onloaded={onloaded} setOnloaded={setOnloaded} />
    );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "70vh", // full viewport height
        fontSize: "3rem", // big text
        fontWeight: "bold",
        color: "#555", // subtle gray color
        textAlign: "center",
      }}
    >
      Laboratory Results is Empty
    </div>
  );
}
